import { ArrowLeft, Mail, MailCheck, RotateCw } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import { ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import type { StepUpMethod } from "@/shared/types/user.entity";
import { formatLockRemaining } from "@/shared/utils/stepUp";

import { EmailCodeIllustration } from "./EmailCodeIllustration";
import { MethodIllustrationStage } from "./MethodIllustrationStage";
import type { EmailCodeChallenge } from "./useEmailCodeChallenge";

interface EmailCodePartProps {
  email: EmailCodeChallenge;
  /** The method the person normally confirms with: names the way back and the question of the offer. */
  method: StepUpMethod;
}

/**
 * Under a method's code field: "Don't have your PIN (or your phone) at hand? Get a code by email". A quiet
 * link, not a button competing with "Verify" — it is the plan B. While the method is locked it pulses,
 * because it is then the only way to confirm the action without waiting.
 */
export function EmailCodeOffer({
  email,
  method,
  emphasize = false,
}: EmailCodePartProps & { emphasize?: boolean }) {
  const { t } = useTranslations("global");
  if (!email.offered && !email.requestError) return null;
  const waiting = email.offerWaitIn > 0;

  return (
    <div className="flex w-full flex-col items-center gap-0.5 border-t border-dashed border-border/80 pt-3 text-center">
      {email.offered ? (
        <>
          <p className="text-[12.5px] text-muted-foreground">
            {t(method === "pin" ? "step_up_email_offer_pin" : "step_up_email_offer_totp")}
          </p>
          <ColocaButton
            variant="link"
            size="sm"
            icon={Mail}
            loading={email.requesting}
            disabled={email.busy || waiting}
            onClick={email.offer}
            className={cn(emphasize && !email.busy && !waiting && "animate-pulse motion-reduce:animate-none")}
          >
            {email.requesting
              ? t("step_up_email_sending")
              : waiting
                ? t("step_up_email_resend_in", { time: formatLockRemaining(email.offerWaitIn) })
                : t("step_up_email_offer_action")}
          </ColocaButton>
        </>
      ) : null}
      {email.requestError ? (
        <p
          role="alert"
          className="animate-fade text-xs text-destructive animate-duration-200 motion-reduce:animate-none"
        >
          {email.requestError}
        </p>
      ) : null}
    </div>
  );
}

export function EmailCodeTitle() {
  const { t } = useTranslations("global");
  return (
    <span className="flex items-center gap-2">
      <MailCheck className="h-5 w-5 text-primary" aria-hidden="true" />
      {t("step_up_email_title")}
    </span>
  );
}

/**
 * The "check your email" face: the envelope delivering its code, the six digits (in clear — it is a one-time
 * code, not a secret the person chose), what went wrong if anything, a resend that waits out the server's
 * minute, and the way back to the usual method. Slides in over the method face it replaces.
 */
export function EmailCodeFace({ email, method, formId }: EmailCodePartProps & { formId: string }) {
  const { t } = useTranslations("global");
  const canResend = email.resendIn === 0 && !email.busy;

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        event.preventDefault();
        email.verify(email.code);
      }}
      className="flex animate-fade-left flex-col items-center gap-3 pt-1 animate-duration-300 motion-reduce:animate-none"
    >
      <MethodIllustrationStage>
        <EmailCodeIllustration />
      </MethodIllustrationStage>

      {/* Keyed by the refusals so a wrong code shakes the row once more, like the method faces. */}
      <div
        key={email.refusals}
        className={cn(email.refusals > 0 && "animate-shake motion-reduce:animate-none")}
      >
        <ColocaOtpInput
          autoFocus
          value={email.code}
          onValueChange={email.setCode}
          onComplete={email.verify}
          invalid={!!email.error}
          disabled={email.busy}
          aria-label={t("step_up_email_code_label")}
        />
      </div>

      {email.error ? (
        <p
          role="alert"
          className="animate-fade text-center text-sm text-destructive animate-duration-200 motion-reduce:animate-none"
        >
          {email.error}
        </p>
      ) : email.notice ? (
        <p
          role="status"
          className="animate-fade text-center text-sm text-success animate-duration-200 motion-reduce:animate-none"
        >
          {email.notice}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
        <ColocaButton
          // A dead code leaves one way on: asking for another, so it stops being a quiet link.
          variant={email.needsNewCode && canResend ? "default" : "ghost"}
          size="sm"
          icon={RotateCw}
          loading={email.requesting}
          disabled={!canResend}
          onClick={email.resend}
          className={cn(
            "tabular-nums",
            email.needsNewCode && canResend && "animate-pulse motion-reduce:animate-none",
          )}
        >
          {email.resendIn > 0
            ? t("step_up_email_resend_in", { time: formatLockRemaining(email.resendIn) })
            : t("step_up_email_resend")}
        </ColocaButton>
        <ColocaButton variant="ghost" size="sm" icon={ArrowLeft} disabled={email.busy} onClick={email.back}>
          {t(method === "pin" ? "step_up_email_back_pin" : "step_up_email_back_totp")}
        </ColocaButton>
      </div>

      <p className="text-center text-xs text-muted-foreground">{t("step_up_email_spam_hint")}</p>
    </form>
  );
}

/** Cancel, then the one primary action: verify the code. */
export function EmailCodeFooter({ email, formId }: { email: EmailCodeChallenge; formId: string }) {
  const { t } = useTranslations("global");
  return (
    <>
      <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={email.busy} />}>
        {t("two_factor_required_modal_cancel")}
      </ColocaDialogClose>
      <ColocaButton
        variant="primary"
        type="submit"
        form={formId}
        loading={email.verifying}
        disabled={email.code.length !== 6 || email.busy}
      >
        {t("two_factor_challenge_submit")}
      </ColocaButton>
    </>
  );
}
